import { useMemo, useContext, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { appNavigate } from '../../utils/appNavigation';
import { useIssueColors, useIssueNavigation } from '../../hooks';
import { ActionsButtons } from '../../components/ActionsButtons';
import { RecentIssuesList } from '../../components/RecentIssuesList';
import PropTypes from 'prop-types';
import AppContext from '../../contexts/AppContext';
import { isInWebView } from '../../utils/deviceUtils';
import useAnalytics from '../../hooks/useAnalytics';

// Resolve the per-service-desk welcome message for the active locale.
// Backward compatible: accepts the locale map ({ fr, en, es }), falling back to
// FR then any provided value; a legacy plain string is rendered as-is; an empty
// or missing config falls back to the translated default. Never throws.
const resolveHelloWorld = (helloWorld, lang, fallback) => {
    if (helloWorld && typeof helloWorld === 'object') {
        return helloWorld[lang] || helloWorld.fr || Object.values(helloWorld).find(Boolean) || fallback;
    }
    if (typeof helloWorld === 'string' && helloWorld.trim()) {
        return helloWorld;
    }
    return fallback;
};

const HomePage = ({ issuesOpen, issuesDraft, issuesToValidate, loading, userName, userServiceDeskName, userRole }) => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const isRequestRoute = location.pathname === '/home' || location.pathname.startsWith('/request');
    const isIssuesRoute = location.pathname.startsWith('/issues');
    const { serviceDesk } = useContext(AppContext);
    const serviceDeskConfig = serviceDesk?.config;
    const welcomeSectionRef = useRef(null);
    const { getStatusColor } = useIssueColors();
    const { handleNavigateToIssue } = useIssueNavigation();
    const deviceIsInWebView = isInWebView();

    useAnalytics({
        currentUser: userName,
        project: userServiceDeskName,
        role: userRole,
        module: 'home',
    });

    const recentIssues = useMemo(() => {
        const allIssues = [...(issuesOpen || []), ...(issuesDraft || []), ...(issuesToValidate || [])];
        return allIssues.sort((a, b) => {
            const dateA = new Date(a.updated || 0);
            const dateB = new Date(b.updated || 0);
            return dateB - dateA;
        });
    }, [issuesOpen, issuesDraft, issuesToValidate]);

    const HEADER_OFFSET = deviceIsInWebView ? 0 : 80;
    const CONTENT_SPACING = 120;

    useLayoutEffect(() => {
        const welcomeSectionElement = welcomeSectionRef.current;

        if (!welcomeSectionElement) {
            console.error('Welcome section element not found');
            return;
        }

        const updateHeight = () => {
            document.documentElement.style.setProperty(
                '--welcome-section-height',
                `${welcomeSectionElement.offsetHeight}px`
            );
        };

        updateHeight();

        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(welcomeSectionElement);

        return () => {
            resizeObserver.disconnect();
            document.documentElement.style.removeProperty('--welcome-section-height');
        };
    }, [serviceDeskConfig?.helloWorld, i18n.language]);

    return (
        <div className="flex flex-col gap-1 relative">
            <div
                ref={welcomeSectionRef}
                className={`fixed ${!deviceIsInWebView ? 'top-20' : 'top-0'} left-0 right-0 p-5 pt-10 pb-32 z-[20] gap-6 flex flex-col bg-black`}
            >
                <div className="w-full max-w-screen-md mx-auto md:px-4">
                    <span
                        className="text-gray-200 text-[32px] font-light leading-tight"
                        dangerouslySetInnerHTML={{ __html: resolveHelloWorld(serviceDeskConfig?.helloWorld, i18n.language, t('Welcome')) }}
                    />
                    <div className="mt-6 mb-6">
                        <ActionsButtons
                            isRequestRoute={isRequestRoute}
                            isIssuesRoute={isIssuesRoute}
                        />
                    </div>
                </div>
            </div>
            <div
                className="z-[100] bg-white rounded-tl-[2rem] rounded-tr-[2rem] -mx-4 md:mx-0"
                style={{
                    marginTop: `calc((var(--welcome-section-height, 420px) + ${HEADER_OFFSET}px) - ${CONTENT_SPACING}px)`
                }}
            >
                <RecentIssuesList
                    serviceDeskConfig={serviceDeskConfig}
                    issues={recentIssues}
                    issuesLoading={loading}
                    onIssueClick={(issueKey, issue) => {
                        handleNavigateToIssue(issueKey, issue);
                        appNavigate('issues', { issueKey });
                    }}
                    getStatusColor={getStatusColor}
                />
            </div>
        </div>
    );
};

HomePage.propTypes = {
    issuesOpen: PropTypes.arrayOf(PropTypes.object).isRequired,
    issuesDraft: PropTypes.arrayOf(PropTypes.object).isRequired,
    issuesToValidate: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export { HomePage };